/**
 * `useTheme` — light/dark switch driven by `.dark` class on <html>.
 *
 * Resolution order on first paint:
 *   1. `localStorage['tc-theme']` if set
 *   2. system `prefers-color-scheme: dark`
 *   3. fall back to 'light'
 *
 * The applied class flip happens in `main.tsx` BEFORE React mounts (inline
 * pre-mount script) so there's no white flash on dark-preferred machines.
 * This hook keeps React state in sync with that class, persists user choice
 * to localStorage, and listens for system-preference changes (only when the
 * user has no explicit preference saved).
 */
import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'tc-theme';

export function readStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') return raw;
  } catch {
    // localStorage may throw in private mode or sandboxed iframes
  }
  return null;
}

export function readSystemTheme(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function resolveInitialTheme(): Theme {
  return readStoredTheme() ?? readSystemTheme();
}

function applyTheme(next: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (next === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export interface UseThemeReturn {
  theme: Theme;
  setTheme: (next: Theme) => void;
  toggleTheme: () => void;
}

export function useTheme(): UseThemeReturn {
  const [theme, setThemeState] = useState<Theme>(() => resolveInitialTheme());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Respond to OS-level theme flips only when the user hasn't expressed a
  // preference — once they click the toggle, the manual choice sticks.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      if (readStoredTheme() !== null) return;
      setThemeState(event.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore: see readStoredTheme()
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  return { theme, setTheme, toggleTheme };
}
